import {
  waypointCountRangeForType,
  type MissionType,
} from '@/lib/mission-type';
import { MISSION_TYPE_LABEL, STATUS_LABEL, aMissionType } from '@/lib/copy';

export interface Waypoint {
  id?: string;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number | null;
  action: string;
  sequence_order: number;
  hover_duration_seconds?: number | null;
}

export interface Mission {
  id: string;
  name: string;
  status: string;
  /**
   * 022. NOT NULL server-side with a DEFAULT of 'route', so it is always present on a row read
   * back from the API — not optional here, because treating it as possibly-absent would spread
   * `?? 'route'` through every surface that reads it and hide a real API change if one ever
   * stopped sending it.
   */
  type: MissionType;
  orbit_radius_m: number | null;
  orbit_direction: 'cw' | 'ccw' | null;
  patrol_laps: number | null;
  hover_seconds: number | null;
  description: string | null;
  default_altitude: number;
  default_speed: number;
  total_distance_meters: number | null;
  assigned_drone_id: string | null;
  assigned_pilot_id: string | null;
  scheduled_at: string | null;
  estimated_duration_minutes: number | null;
  site_id: string | null;
  drone_callsign: string | null;
  pilot_name: string | null;
  site_name: string | null;
  /** See `MissionCoverageHold` — the same field the list shape carries. */
  coverage_held_open?: boolean;
  /**
   * Is continuous coverage attached at all — see `missionCoverageLock`. NOT the same question as
   * `coverage_held_open`, and the two must not be used interchangeably: a covered mission with an
   * aircraft on task is attached and not held open.
   */
  coverage_attached?: boolean;
  /**
   * Is this mission's assigned aircraft in the air right now (#574)?
   *
   * The server's own answer, resolved by the predicate STOP branches on, and served by
   * `GET /api/missions/:id` alone — see `missionAirborneCompleteLock`. Optional because it is
   * absent on the list payload, on an older server, and whenever the live-state read failed:
   * absent is "no claim", never "nothing is flying".
   */
  aircraft_airborne?: boolean;
  waypoints: Waypoint[];
}

export interface Conflict {
  mission_id: string;
  mission_name: string;
  reason: string;
  /**
   * How much this conflict means — and now the difference between "your launch will be refused"
   * and "noted". `GET /:id/deconflict` has always returned it; the client discarded it and drew
   * every row identically amber, which was merely cosmetic while a same-aircraft clash was
   * always `hard`. Since only an aircraft ALREADY FLYING earns a refusal, a card showing three
   * amber rows cannot otherwise tell the operator which one stops them.
   *
   * Optional because an older server, or a cached response, may not carry it — an unknown
   * severity is drawn as advisory rather than claimed to block.
   */
  severity?: 'hard' | 'advisory';
}

/** Does this conflict refuse a launch outright? Only `hard` does; anything else is advice. */
export function conflictBlocksLaunch(conflict: Conflict): boolean {
  return conflict.severity === 'hard';
}

/**
 * Partial `PUT /api/missions/:id` body the mission page can send. `null` clears a field, an
 * omitted key leaves it alone — the five assignment/scheduling columns are `.nullable()` server
 * side precisely so those two intents are distinguishable. `default_altitude` / `default_speed`
 * are only ever a number or absent — not because the column forbids null (it does not; it is
 * `DOUBLE PRECISION DEFAULT 50/5` with no NOT NULL) but because the API refuses null for them and
 * every write path supplies a value.
 */
export interface MissionFieldPatch {
  description?: string | null;
  assigned_drone_id?: string | null;
  assigned_pilot_id?: string | null;
  site_id?: string | null;
  scheduled_at?: string | null;
  estimated_duration_minutes?: number | null;
  default_altitude?: number;
  default_speed?: number;
}

/**
 * Which sidebar panel a field save came from. Busy state is scoped per panel so saving a
 * description does not freeze — or relabel — the unrelated assignment and parameter controls.
 */
export type MissionFieldPanel = 'assignments' | 'params' | 'description';

/** The fleet reference lists the assignment dropdowns are built from. */
export type MissionReferenceKey = 'drones' | 'pilots' | 'sites';

/**
 * Per-list fetch state. `error` and `ready`-with-nothing are different situations and must read
 * differently: an org with 40 drones must never be told to go add one because a request failed.
 */
export type MissionListStatus = 'loading' | 'ready' | 'error';

/** Statuses that close a mission to edits — the map, the route and every editor go read-only. */
export const READONLY_MISSION_STATUSES: readonly string[] = ['completed', 'cancelled'];

export function isMissionEditable(status: string): boolean {
  return !READONLY_MISSION_STATUSES.includes(status);
}

/**
 * Statuses whose ROUTE may still be redrawn. Narrower than `isMissionEditable` on purpose.
 *
 * An in-progress mission stays editable in general — annotating or re-timing a sortie that is in
 * the air is legitimate — but its route is not. The aircraft is flying the plan it was uploaded;
 * rewriting the stored waypoints changes nothing in the air and leaves the briefing and the
 * after-action record showing a route nobody flew. The API enforces this
 * (`EDITABLE_ROUTE_STATUSES` in `src/api/missions.ts`); this keeps the UI from offering an edit
 * that can only end in a 409.
 */
export function isRouteEditable(status: string): boolean {
  return status === 'draft' || status === 'scheduled';
}

export interface FieldBounds { min: number; max: number }

/** Mirrored from `createSchema` in `src/api/missions.ts` — the API rejects anything outside these. */
export const ALTITUDE_BOUNDS: FieldBounds = { min: 10, max: 400 };
export const SPEED_BOUNDS: FieldBounds = { min: 1, max: 20 };
export const DURATION_BOUNDS: FieldBounds = { min: 1, max: 480 };

/**
 * Parses a numeric flight parameter. Blank, unparseable and out-of-range input all return null so
 * the caller can name the offending field — an emptied altitude or speed box must never silently
 * write a default over the value a mission is already planned around.
 */
export function parseBoundedNumber(raw: string, bounds: FieldBounds): number | null {
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value)) return null;
  if (value < bounds.min || value > bounds.max) return null;
  return value;
}

/** ISO timestamp → the `YYYY-MM-DDTHH:mm` a `datetime-local` input carries, read as UTC. */
export function isoToUtcInputValue(iso: string | null): string {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toISOString().slice(0, 16);
}

/**
 * Exactly what a `datetime-local` box carries. Matched explicitly because `Date.parse` falls back
 * to an implementation-defined parser when a string is not valid ISO 8601, and V8's reads both
 * `':00Z'` (an empty box) and `'not-a-date:00Z'` as midnight on 1 Jan 2000 — a cleared departure
 * would have been stored as the year 2000 rather than rejected.
 */
const UTC_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/** `YYYY-MM-DDTHH:mm` (UTC) → ISO timestamp for the API; null when the browser gave us junk. */
export function utcInputValueToIso(value: string): string | null {
  // Browsers append seconds when the step allows them; the field's resolution is minutes.
  const minutes = value.slice(0, 16);
  if (!UTC_INPUT_PATTERN.test(minutes)) return null;
  const ms = Date.parse(`${minutes}:00Z`);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString();
}

/**
 * Every value `PUT /:id/waypoints` would persist, flattened in route order.
 *
 * Route dirtiness cannot be a length comparison: dragging a waypoint — or deleting one and
 * placing a better one — leaves the count identical while changing where the drone flies. A
 * cardinal check calls that clean, so the readiness panel goes green and the mission is scheduled
 * against the coordinates still stored on the server, with the map showing the dragged ones.
 */
export function routeSignature(waypoints: Waypoint[]): string {
  return waypoints
    .map((w) => [
      w.latitude, w.longitude, w.altitude,
      w.speed ?? '', w.action, w.hover_duration_seconds ?? '',
    ].join(','))
    .join(';');
}

/** Waypoints the API demands before a mission may leave `draft`. */
export const MIN_SCHEDULE_WAYPOINTS = 2;

export interface ScheduleReadinessItem {
  key: 'drone' | 'pilot' | 'waypoints';
  /** Shown when the check passes. */
  label: string;
  /** Shown when it does not — the operator's next action, not a diagnosis. */
  blocker: string;
  satisfied: boolean;
  /**
   * True when failing this check does NOT stop the API from scheduling. Advisory items are still
   * worth rendering — an operator wants to know the sortie has no crew yet — but they must never
   * feed `ready`, or the UI refuses what `PUT /api/missions/:id` would happily accept.
   */
  advisory: boolean;
}

export interface ScheduleReadiness {
  /** Mirrors the server gate exactly: nothing but the waypoint count can make this false. */
  ready: boolean;
  /** Unmet checks the API really does enforce. Empty ⇒ `ready`. */
  blockers: string[];
  /** Unmet advisory checks — shown, never blocking. */
  advisories: string[];
  items: ScheduleReadinessItem[];
}

/**
 * What `PUT /api/missions/:id` requires before allowing `draft → scheduled`, plus the two crew
 * facts worth showing beside it.
 *
 * The server gate is now ONE check: at least `MIN_SCHEDULE_WAYPOINTS` rows in `mission_waypoints`.
 * Drone and pilot used to be enforced here too and no longer are — see the comment on the
 * `newStatus === 'scheduled'` branch in `src/api/missions.ts`: crew is enforced at LAUNCH, where
 * the aircraft genuinely has to exist, because requiring it at scheduling made it impossible to
 * plan next week's sortie before anyone knew which airframe would be free. They stay in `items`
 * as `advisory` so the planning UI keeps reporting them, but they are excluded from `blockers`
 * and from `ready`: this function exists so the UI can never disagree with the API in either
 * direction, and blocking on crew would now be a disagreement.
 *
 * Site is deliberately absent: the API has never required one.
 *
 * `waypointCount` MUST be the count the server holds, never the length of the route the operator
 * is still drawing — the API counts rows in `mission_waypoints`, so feeding it the draft is how
 * the checklist ends up green on a mission that is about to be rejected with a 400.
 */
export function scheduleReadiness(
  mission: Pick<Mission, 'assigned_drone_id' | 'assigned_pilot_id'>,
  waypointCount: number,
  /**
   * 022. Trailing and defaulted to `route` — the column's own default and what every mission
   * was before types existed — so every existing caller keeps the 2-waypoint gate it had.
   */
  missionType: MissionType = 'route',
): ScheduleReadiness {
  // The server's gate is the TYPE's minimum now (see the `newStatus === 'scheduled'` branch in
  // src/api/missions.ts). A flat 2 here would tell an operator planning a GO TO POINT to add a
  // waypoint the API would then refuse — the exact UI/API disagreement this function exists to
  // prevent, just in the opposite direction to the one it was written for.
  const { min: minWaypoints, max: maxWaypoints } = waypointCountRangeForType(missionType);
  const items: ScheduleReadinessItem[] = [
    {
      key: 'drone',
      label: 'Drone assigned',
      blocker: 'No drone assigned — optional to schedule',
      satisfied: Boolean(mission.assigned_drone_id),
      advisory: true,
    },
    {
      key: 'pilot',
      label: 'Pilot assigned',
      blocker: 'No pilot assigned — optional to schedule',
      satisfied: Boolean(mission.assigned_pilot_id),
      advisory: true,
    },
    {
      key: 'waypoints',
      label: minWaypoints === 1
        ? (waypointCount === 1 ? 'Point placed' : `Route has ${waypointCount} waypoints`)
        : `Route has ${waypointCount} waypoints`,
      // The OVER-cap refusal reads off `maxWaypoints`, not `minWaypoints`. Both are 1 for a GO
      // TO POINT and an ORBIT, so keying on the minimum was right until a type had min 1 and a
      // max above it — OUT & BACK already did, and #373 gave HOVER the same shape. Told "a
      // HOVER / LOITER takes one point (101 placed)", an operator would delete a route the type
      // is perfectly happy to fly and still not be under the limit.
      blocker: minWaypoints === 1
        ? (waypointCount > maxWaypoints
            ? (maxWaypoints === 1
                ? `${aMissionType(missionType, { capitalised: true })} takes one point (${waypointCount} placed)`
                : `${aMissionType(missionType, { capitalised: true })} takes at most ${maxWaypoints} waypoints (${waypointCount} placed)`)
            // "the point" only when there is exactly one to place. A type with min 1 and a max
            // above it (OUT & BACK, and HOVER since #373) sits under a section headed Route
            // whose hint reads "Click waypoints on the map" — telling the operator to place
            // "the point" in the same breath leaves the panel unable to say whether this type
            // takes one point or many, which is the question the type picker exists to answer.
            : maxWaypoints === 1
              ? `Place the point for this ${MISSION_TYPE_LABEL[missionType].toLowerCase()}`
              : `Place at least one waypoint for this ${MISSION_TYPE_LABEL[missionType].toLowerCase()}`)
        : `Add at least ${minWaypoints} waypoints (${waypointCount} placed)`,
      satisfied: waypointCount >= minWaypoints && waypointCount <= maxWaypoints,
      advisory: false,
    },
  ];
  const unmet = items.filter((item) => !item.satisfied);
  const blockers = unmet.filter((item) => !item.advisory).map((item) => item.blocker);
  const advisories = unmet.filter((item) => item.advisory).map((item) => item.blocker);
  return { ready: blockers.length === 0, blockers, advisories, items };
}

/**
 * The mission statuses, from the one place that holds them.
 *
 * This map used to spell the same five words `StatusBadge` already had, so a rename could land
 * on one surface and not the other. `STATUS_LABEL` in `lib/copy` is now the only copy of them.
 */
export const STATUS_LABELS: Record<string, string> = STATUS_LABEL;

/**
 * Mission status → `StatusBadge` tone. Identity now, because `StatusBadge` carries the mission
 * vocabulary directly: the badge label is the status, so nothing needs to print a second word
 * beside it. Previously `scheduled` mapped to `active` and `in_progress` to `flying`, which
 * rendered byte-identical green chips — a mission planned for Thursday looked like one in the
 * air. Green is now reserved for `in_progress`.
 */
export const STATUS_BADGE_MAP: Record<string, 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled'> = {
  draft: 'draft', scheduled: 'scheduled', in_progress: 'in_progress', completed: 'completed', cancelled: 'cancelled',
};

/**
 * The shape every mission surface needs in order to say whether anything is actually flying.
 *
 * `coverage_held_open` is computed server-side (`withCoverageHold` in `src/api/missions.ts`,
 * over the Coverage module's `heldOpenMissionIds`) and means: continuous coverage is attached,
 * the mission has flown at least one sortie, and none of them is open. The client cannot
 * derive it — it is a fact about `mission_coverage` and `flights` rows, neither of which any
 * mission payload carries.
 *
 * OPTIONAL, AND ABSENT IS A REAL THIRD ANSWER (#471). It means "the server could not determine
 * this" — an older server, a cached response, or a coverage lookup that threw. Both transports
 * now say so the same way: `coverageFactsForMission` omits the field on the event path, and
 * `withCoverageFacts` omits it on the read path rather than answering `false`.
 *
 * IT USED TO BE READ AS "NOT HELD", which is what #471 was. `false` here is not a smaller claim
 * than `true`, it is the OPPOSITE claim: it puts a green IN PROGRESS badge and a counting
 * "Elapsed since launch" clock over an aircraft that is parked on its pad. Absent is now drawn
 * as its own state by every surface that DISPLAYS it — see `missionSortieState`.
 */
export interface MissionCoverageHold {
  status: string;
  coverage_held_open?: boolean;
}

/**
 * What this mission's sky actually contains — including "I do not know" (#471).
 *
 * THE DISPLAY PREDICATE. Three states, because the server has three answers:
 *
 * - `airborne`     — running, and a flight of it is open.
 * - `held_open`    — running, covered, and nothing is up. A normal steady state.
 * - `unknown`      — running, and the coverage lookup failed. NOT a claim either way.
 * - `null`         — not running at all; the question does not arise.
 *
 * Every surface that DRAWS the sortie state asks this, and none of them may collapse `unknown`
 * into one of the other two: drawing it as `airborne` is the #400 misread (a green badge over an
 * empty station), and drawing it as `held_open` asserts a station is empty when it may have an
 * aircraft on task. Both are assertions the server explicitly declined to make.
 *
 * FOR DISPLAY, NOT FOR GATING — the split is deliberate and is the whole shape of #471's fix.
 * A control that could COMMAND AN AIRCRAFT asks `missionSortieAirborne` instead, which resolves
 * `unknown` to the conservative answer. See that function.
 */
export function missionSortieState(
  m: MissionCoverageHold,
): 'airborne' | 'held_open' | 'unknown' | null {
  if (m.status !== 'in_progress') return null;
  if (m.coverage_held_open === undefined) return 'unknown';
  return m.coverage_held_open ? 'held_open' : 'airborne';
}

/**
 * Is a sortie of this mission IN THE AIR right now — assuming the worst when nobody knows?
 *
 * The one predicate behind every "in the air" assertion on every mission surface, and the
 * reason it exists: `status === 'in_progress'` stopped meaning "airborne" when continuous
 * coverage (#248) began deliberately holding a mission open past its aircraft's landing. A
 * covered station between sorties is `in_progress` with an empty sky, and three surfaces were
 * each re-deriving "airborne" from the status alone — a green badge, a running
 * elapsed-since-launch clock, and a "route locked because it is flying" notice, all over
 * nothing.
 *
 * IT KEEPS ITS PERMISSIVE DEFAULT ON PURPOSE (#471). `!== true` means an UNKNOWN hold reads as
 * airborne, and for the surfaces still asking this that is the SAFE direction, not an oversight:
 * they are the ones deciding whether a press commands an aircraft home. An operator warned that
 * STOP may fly an aircraft back, on a mission where nothing was up, has lost nothing. The
 * reverse — a silent STOP on a mission whose sky the client could not read — is an unannounced
 * recovery, and that is the failure this default buys insurance against.
 *
 * SO: GATES ASK THIS, DISPLAY ASKS `missionSortieState`. A surface that merely draws the state
 * and reaches for this instead reintroduces #471 at that call site.
 */
export function missionSortieAirborne(m: MissionCoverageHold): boolean {
  return m.status === 'in_progress' && m.coverage_held_open !== true;
}

/**
 * The `StatusBadge` tone for a mission, once the hold is taken into account.
 *
 * `STATUS_BADGE_MAP` is still the mapping for every other status; this adds the two cases the
 * status alone cannot express. Kept beside it rather than inlined at each call site so a
 * fourth mission surface cannot quietly reintroduce the green badge.
 *
 * `coverage_unknown` IS GRAY, NOT AMBER, and the reason is in `StatusBadge` at length: amber
 * already belongs to `covered_no_sortie`, which is a NORMAL steady state. Painting "the database
 * did not answer" the same colour as "the station is manned and between sorties" makes normal
 * and broken indistinguishable in the <3-second scan the palette exists to serve.
 */
export function missionBadgeStatus(
  m: MissionCoverageHold,
): 'draft' | 'scheduled' | 'in_progress' | 'covered_no_sortie' | 'coverage_unknown' | 'completed' | 'cancelled' {
  const sortie = missionSortieState(m);
  if (sortie === 'held_open') return 'covered_no_sortie';
  if (sortie === 'unknown') return 'coverage_unknown';
  return STATUS_BADGE_MAP[m.status] ?? 'draft';
}

/**
 * Why the terminal buttons are refused, in the operator's words — or null when they are not.
 *
 * REFUSED BEFORE THE PRESS, NOT AFTER, which is the rule the Start button already follows two
 * cases above: `PUT /api/missions/:id` returns 409 on `completed` and on `cancelled` while
 * coverage is attached (#391), and a button whose only outcome is an error toast is worse than a
 * named blocker. It also has to say where the door actually is — an operator told only "no" on a
 * mission they are trying to finish has nowhere to go.
 *
 * A STRING HELPER RATHER THAN JSX, for the same reason `missionRouteLockNotice` below is one:
 * the claim is testable without mounting the page.
 *
 * READS `coverage_attached`, NEVER `coverage_held_open`. The held-open predicate means "covered
 * with an empty sky", so a rotation actually flying would read as unlocked — the one mission
 * this must never let through.
 *
 * IT SAYS "ATTACHED", NOT "COVERED", AND THE DIFFERENCE IS NOT PEDANTRY. The gate asks whether a
 * `mission_coverage` row EXISTS, whatever state it is in — so this sentence also appears on a
 * `suspended`, `stopped` or `pending_station` rotation, where the station is provably NOT manned
 * and `COVERAGE_ROW_PRESENTATION` deliberately draws no COVERED chip at all. Saying "covered"
 * there asserted the one thing the coverage vocabulary exists to keep honest. On a `gap` it was
 * worse: an amber COVERED sat beside the red GAP an operator was being told to act on.
 *
 * AND ABSENT LOCKS TOO (#471), for the reason `missionDeleteBlocker` spells out on the delete
 * gate: the server omits `coverage_attached` when its own lookup failed, and `PUT /:id` does that
 * same lookup and 409s when it throws. Reading absent as "nothing attached" therefore offers a
 * terminal transition the server is about to refuse — the one outcome the rule at the top of this
 * comment exists to prevent. Its own sentence, not this one, because "detach it" is the wrong
 * instruction when the platform cannot say anything is attached.
 *
 * THE RETRY INSTRUCTION IS THE CALLER'S TO DECLARE, because it is a fact about the SURFACE and not
 * about the mission. Both consumers render this string and they refresh in opposite ways:
 *
 *   - `CopMissionsPanel` re-polls every 30 s (`QUEUE_REFRESH_MS`) and takes `mission_event` frames,
 *     so waiting genuinely clears the block. It takes the default.
 *   - The mission page fetches ONCE - `useEffect(() => { void loadMission(); }, [loadMission])`,
 *     no interval, no `subscribeMissionEvent` - and the only things that call `loadMission` again
 *     are a save and the terminal transitions, which are precisely what this lock disables. An
 *     operator told to wait there would wait for ever. It passes `'manual'`.
 *
 * IT USED TO SAY "RELOAD" UNCONDITIONALLY, on the reasoning that this lock renders on the mission
 * page alone. That was true when written and false by the end of the same branch: threading the
 * unread state through `coverageLockFor` (#471) put this exact string on the COP row, where the
 * instruction is wrong twice - waiting works, and the control the sentence hangs on is a link to
 * `/missions/:id` rather than a reload. An operator told to reload during an incident either
 * leaves the live air picture or hard-reloads the COP.
 */
export function missionCoverageLock(
  m: { coverage_attached?: boolean },
  /** How the surface rendering this string gets a fresh answer. Defaults to the common case. */
  refresh: 'polls' | 'manual' = 'polls',
): string | null {
  if (m.coverage_attached === true) return 'COVERAGE ATTACHED — detach it to end this mission';
  if (m.coverage_attached === undefined) {
    return refresh === 'manual'
      ? 'COVERAGE UNREAD — reload to retry'
      : 'COVERAGE UNREAD — retry in a moment';
  }
  return null;
}

/**
 * Why COMPLETE is refused while the aircraft is up, or null (#574).
 *
 * A SECOND LOCK RATHER THAN A WIDER ONE, and the split is the point. `missionCoverageLock`
 * refuses BOTH terminal transitions; this refuses Complete alone. `closeFlight` writes
 * `completed` and never `cancelled`, so the two are not the same act on a live sortie:
 * completing claims the mission is over while the aircraft is still flying it, whereas
 * cancelling records that it was called off, which is true. Folding this into the shared lock
 * would leave an operator aborting a sortie with no way to record it as anything but
 * `completed` — a worse lie than the one being fixed.
 *
 * REFUSED BEFORE THE PRESS, which is this file's standing rule: `PUT /api/missions/:id` now
 * 409s `aircraft_airborne` on exactly this transition, and a button that gets refused is worse
 * than one that says why.
 *
 * KEYED ON A SERVER FACT, NOT ON `missionSortieAirborne`. That predicate is `in_progress &&
 * !coverage_held_open`, which the COP uses to LABEL rows and which is wrong as a GATE in the
 * direction that costs an operator their only door: a mission stranded `in_progress` by a failed
 * automatic close-out has nothing in the air, and would be refused for ever with the manual
 * Complete that exists precisely for that case greyed out. `GET /api/missions/:id` resolves the
 * real thing (`aircraft_airborne`) with the same predicate STOP branches on.
 *
 * ABSENT MEANS NO CLAIM, never "nothing is flying" - an older server, a list payload, or a
 * failed live-state read all leave it undefined, and the server's own 409 is still the gate.
 */
export function missionAirborneCompleteLock(m: { aircraft_airborne?: boolean }): string | null {
  return m.aircraft_airborne === true
    ? 'IN THE AIR — use STOP on the COP to bring the aircraft home and end this mission'
    : null;
}

/**
 * Why the route on the mission page cannot be edited, in the operator's words.
 *
 * A STRING HELPER RATHER THAN JSX, so the claim is testable without mounting the page — the
 * mission page has no component test, and this overlay sits on the map an operator is reading
 * while deciding whether to launch the next aircraft in a rotation, which makes it the worst
 * place in the product for a wrong claim to hide behind an untested branch.
 *
 * The lock itself is the same in the first two cases — the server refuses a route edit on any
 * `in_progress` mission with a 409 — but the REASON is not, and only one of the two is ever
 * true at a time. A covered station between sorties is `in_progress` with nothing in the air.
 */
export function missionRouteLockNotice(m: MissionCoverageHold): string {
  if (m.status !== 'in_progress') return `READ-ONLY: Mission ${m.status}`;
  // A THIRD REASON, because there is a third answer (#471). This is DISPLAY — it explains a lock
  // that holds in all three cases anyway — so it asks `missionSortieState` and reports the
  // unknown as unknown. Claiming "IN THE AIR" off a coverage read that failed is the same false
  // assertion on this page that the green badge was on the COP.
  const state = missionSortieState(m);
  if (state === 'airborne') return 'IN THE AIR — ROUTE LOCKED';
  if (state === 'unknown') return 'RUNNING — COVERAGE UNREAD — ROUTE LOCKED';
  return 'COVERED — NO SORTIE — ROUTE LOCKED';
}

export const VALID_TRANSITIONS: Record<string, string[]> = {
  draft: ['scheduled'],
  scheduled: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};

export function formatDistance(m: number | null): string {
  if (!m) return '-';
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

export function estimateFlightTime(distanceM: number | null, speedMs: number): string {
  if (!distanceM || speedMs <= 0) return '-';
  const seconds = distanceM / speedMs;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds % 60);
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
}

export function estimateBattery(distanceM: number | null, speedMs: number): string {
  if (!distanceM || speedMs <= 0) return '-';
  const flightMinutes = distanceM / speedMs / 60;
  const pct = Math.round((flightMinutes / 25) * 100);
  return `~${Math.min(pct, 100)}%`;
}
